import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Responsive as ResponsiveGridLayout } from 'react-grid-layout';
import 'react-grid-layout/css/styles.css';
import 'react-resizable/css/styles.css';
import { backendService } from '../services/backendService';

// ─── Default Layout ─────────────────────────────────────────────────────────
const DEFAULT_LAYOUT = [
  { i: 'directory', x: 0, y: 0, w: 3, h: 12 },
  { i: 'quick_actions', x: 3, y: 0, w: 9, h: 3 },
  { i: 'execution', x: 3, y: 3, w: 4, h: 9 },
  { i: 'results', x: 7, y: 3, w: 5, h: 9 }
];

// ─── Types ─────────────────────────────────────────────────────────────────

interface MCPTool {
  name: string;
  description: string;
  server: string;
  parameters?: {
    type: string;
    properties?: Record<string, { type: string; description: string; default?: any }>;
    required?: string[];
  };
}

interface ExecutionResult {
  tool: string;
  server: string;
  result: any;
  error?: string;
  timestamp: string;
  duration_ms: number;
}

// ─── Server colours ─────────────────────────────────────────────────────────

const SERVER_COLORS: Record<string, string> = {
  'browser-use': '#f59e0b',
  'desktop-commander': '#3b82f6',
  'android-mcp': '#22c55e',
  'nexus-memory': '#a855f7',
  'nexus-vision': '#06b6d4',
  'nexus-voice': '#f97316',
  'nexus-ollama': '#84cc16',
  'nexus-telebit': '#ec4899',
  'nexus-system': '#ef4444',
  'nexus-files': '#8b5cf6',
};

const SERVER_ICONS: Record<string, string> = {
  'browser-use': '🌐',
  'desktop-commander': '🖥️',
  'android-mcp': '📱',
  'nexus-memory': '🧠',
  'nexus-vision': '👁️',
  'nexus-voice': '🎙️',
  'nexus-ollama': '🤖',
  'nexus-telebit': '🔗',
  'nexus-system': '⚙️',
  'nexus-files': '📁',
};

// ─── Quick Actions ───────────────────────────────────────────────────────────

const QUICK_ACTIONS = [
  { label: 'System Metrics', server: 'nexus-system', tool: 'system_metrics', args: {} },
  { label: 'Ollama Models', server: 'nexus-ollama', tool: 'ollama_list_models', args: {} },
  { label: 'Telebit Status', server: 'nexus-telebit', tool: 'telebit_status', args: {} },
  { label: 'TTS Status', server: 'nexus-voice', tool: 'tts_status', args: {} },
  { label: 'ADB Devices', server: 'android-mcp', tool: 'list_devices', args: {} },
  { label: 'Memory Stats', server: 'nexus-memory', tool: 'memory_stats', args: {} },
  { label: 'Docker List', server: 'desktop-commander', tool: 'docker_list', args: {} },
  { label: 'Disk Usage', server: 'nexus-system', tool: 'system_disk_usage', args: { path: '/' } },
];

// ─── Component ───────────────────────────────────────────────────────────────

const MCPToolsPanel: React.FC = () => {
  const [servers, setServers] = useState<string[]>([]);
  const [tools, setTools] = useState<MCPTool[]>([]);
  const [selectedServer, setSelectedServer] = useState<string>('all');
  const [selectedTool, setSelectedTool] = useState<MCPTool | null>(null);
  const [params, setParams] = useState<Record<string, any>>({});
  const [results, setResults] = useState<ExecutionResult[]>([]);
  const [loading, setLoading] = useState(false);
  const [toolsLoading, setToolsLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');

  // Draggable Layout State
  const [isLocked, setIsLocked] = useState(true);
  const [layouts, setLayouts] = useState<Record<string, any[]>>({ lg: DEFAULT_LAYOUT });
  const [mounted, setMounted] = useState(false);

  // Custom width provider logic
  const [width, setWidth] = useState(1200);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setMounted(true);
    const saved = localStorage.getItem('omnigate_mcp_layout');
    if (saved) {
      try {
        const p = JSON.parse(saved);
        if (p) { Object.keys(p).forEach(k => { if (Array.isArray(p[k])) p[k].forEach((i: any) => delete i.static); }); }
        setLayouts(p);
      } catch { }
    }

    if (containerRef.current) {
      setWidth(containerRef.current.clientWidth);
    }

    const resizeObserver = new ResizeObserver(entries => {
      for (let entry of entries) {
        if (entry.contentRect.width > 0) {
          setWidth(entry.contentRect.width);
        }
      }
    });

    if (containerRef.current) {
      resizeObserver.observe(containerRef.current);
    }

    return () => resizeObserver.disconnect();
  }, []);

  const onLayoutChange = (layout: any, allLayouts: any) => {
    setLayouts(allLayouts);
    localStorage.setItem('omnigate_mcp_layout', JSON.stringify(allLayouts));
  };

  // ── Load servers + tools ─────────────────────────────────────────────────

  const loadTools = useCallback(async () => {
    setToolsLoading(true);
    try {
      const serverList = await backendService.getOmnigateServers();
      const serverNames = serverList.map((s: any) => (typeof s === 'string' ? s : s.name));
      setServers(serverNames);

      const allTools = await backendService.getOmnigateTools();
      setTools(allTools);
    } catch (e) {
      console.error('Failed to load MCP tools:', e);
    } finally {
      setToolsLoading(false);
    }
  }, []);

  useEffect(() => { loadTools(); }, [loadTools]);

  // ── Execute ───────────────────────────────────────────────────────────────

  const executeTool = useCallback(async (server: string, toolName: string, args: Record<string, any>) => {
    setLoading(true);
    const startTime = Date.now();
    try {
      const result = await backendService.executeOmnigateTool(toolName, server, args);
      const entry: ExecutionResult = {
        tool: toolName,
        server,
        result,
        timestamp: new Date().toLocaleTimeString(),
        duration_ms: Date.now() - startTime,
      };
      setResults(prev => [entry, ...prev].slice(0, 50));
      return entry;
    } catch (err: any) {
      const entry: ExecutionResult = {
        tool: toolName,
        server,
        result: null,
        error: err.message || String(err),
        timestamp: new Date().toLocaleTimeString(),
        duration_ms: Date.now() - startTime,
      };
      setResults(prev => [entry, ...prev].slice(0, 50));
    } finally {
      setLoading(false);
    }
  }, []);

  const handleExecuteSelected = () => {
    if (!selectedTool) return;
    executeTool(selectedTool.server, selectedTool.name, params);
  };

  // ── Filtered tools ────────────────────────────────────────────────────────

  const filteredTools = tools.filter(t => {
    const matchServer = selectedServer === 'all' || t.server === selectedServer;
    const matchSearch = !searchQuery ||
      t.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      t.description.toLowerCase().includes(searchQuery.toLowerCase());
    return matchServer && matchSearch;
  });

  // ── Render ────────────────────────────────────────────────────────────────

  if (!mounted) return null;

  return (
    <div ref={containerRef} className="flex flex-col h-full bg-[#131722] overflow-hidden min-h-[600px] relative pb-10">
      <style>{`
          .react-grid-layout { position: relative; transition: height 200ms ease; }
          .react-grid-item { transition: all 200ms ease; transition-property: left, top; }
          .react-grid-item.cssTransforms { transition-property: transform; }
          .react-grid-item.resizing { z-index: 1; will-change: width, height; }
          .react-grid-item.react-draggable-dragging { transition: none; z-index: 3; will-change: transform; }
          .react-grid-item.react-grid-placeholder { background: rgba(59, 130, 246, 0.2); border: 2px dashed rgba(59, 130, 246, 0.5); opacity: 0.5; z-index: 2; transition: none; }
          .drag-handle { cursor: grab; }
          .drag-handle:active { cursor: grabbing; }
          .panel-wrapper { height: 100%; display: flex; flex-direction: column; background: #1e222d; border: 1px solid #2a2e39; border-radius: 0.5rem; overflow: hidden; box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.5); }
          .panel-wrapper:hover { border-color: rgba(59, 130, 246, 0.3); }
      `}</style>

      <div className="absolute -top-10 right-4 z-50 flex gap-2">
        <button onClick={() => setIsLocked(!isLocked)} className={`text-[9px] px-2 py-1 rounded border transition-colors ${isLocked ? 'bg-red-900/40 text-red-400 border-red-800' : 'bg-slate-800 text-slate-400 border-slate-700 hover:text-white'}`}>
          <i className={`fas ${isLocked ? 'fa-lock' : 'fa-unlock'} mr-1`}></i> {isLocked ? 'Locked' : 'Unlocked'}
        </button>
        <button onClick={() => { setLayouts({ lg: DEFAULT_LAYOUT }); localStorage.removeItem('omnigate_mcp_layout'); }} className="text-[9px] bg-slate-800 text-slate-400 hover:text-white px-2 py-1 rounded border border-slate-700">
          <i className="fas fa-sync-alt mr-1"></i> Reset Layout
        </button>
      </div>

      <div className={`flex justify-between items-start mb-2 px-2 z-10 ${!isLocked ? 'drag-handle' : 'cursor-default'} flex-shrink-0`}>
        <div>
          <h2 className="text-xl font-black text-white uppercase italic tracking-tighter">Model <span className="text-blue-500">Context</span> Protocol</h2>
          <p className="text-[10px] text-slate-500 font-bold uppercase tracking-widest mt-1">Cross-Environment Toolkit</p>
        </div>
      </div>


      <div className="flex justify-between items-center sm:mb-4 mb-2 px-2 bg-[#131722]/80 border border-[#2a2e39] rounded py-2">
        <div className="flex items-center gap-2">
          <button onClick={() => setIsLocked(!isLocked)} className={`text-[10px] px-3 py-1.5 rounded transition-all font-bold ${isLocked ? 'bg-red-500/20 text-red-400 border border-red-500/50 shadow-[0_0_10px_rgba(239,68,68,0.2)]' : 'bg-green-500/20 text-green-400 border border-green-500/50'}`}>
            <i className={`fas ${isLocked ? 'fa-lock' : 'fa-unlock'} mr-1`}></i> {isLocked ? 'Blokada Włączona' : 'Odblokowane (Przeciągaj)'}
          </button>
        </div>
      </div>
      <ResponsiveGridLayout
        className="layout flex-1"
        width={width}
        layouts={isLocked ? Object.fromEntries(Object.entries(layouts).map(([k, v]) => [k, (v as any[]).map((i: any) => ({ ...i, static: true }))])) : layouts}
        breakpoints={{ lg: 1200, md: 996, sm: 768, xs: 480, xxs: 0 }}
        cols={{ lg: 12, md: 10, sm: 6, xs: 4, xxs: 2 }}
        rowHeight={50}
        onLayoutChange={onLayoutChange}
        // @ts-ignore
        isDraggable={!isLocked}
        // @ts-ignore
        isResizable={!isLocked}
        // @ts-ignore
        draggableHandle=".drag-handle"
        margin={[12, 12]}
      >
        {/* DIRECTORY PANEL */}
        <div key="directory" className="panel-wrapper">
          <div className={`p-2 border-b border-[#2a2e39] bg-[#131722]/60 flex items-center justify-between ${!isLocked ? 'drag-handle' : 'cursor-default'} flex-shrink-0`}>
            <h2 className="text-[10px] font-black text-white uppercase tracking-widest">
              🔧 Directory
            </h2>
            <div className="flex items-center gap-2">
              <button onClick={loadTools} className="text-[9px] text-slate-500 hover:text-blue-400 transition-colors">
                ↺ Refresh
              </button>
              <i className="fas fa-grip-lines text-slate-600"></i>
            </div>
          </div>

          <div className="p-2 border-b border-[#2a2e39] bg-[#1a1d27]">
            <input
              type="text"
              placeholder="Search tools..."
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
              className="w-full bg-[#0a0c10] border border-[#2a2e39] rounded px-2 py-1 text-[10px] text-slate-300 placeholder-slate-600 focus:outline-none focus:border-blue-500"
            />
            <div className="flex flex-wrap gap-1 mt-2">
              <button
                onClick={() => setSelectedServer('all')}
                className={`text-[7px] px-1.5 py-0.5 rounded font-bold uppercase transition-colors ${selectedServer === 'all' ? 'bg-blue-600 text-white' : 'bg-[#0a0c10] text-slate-500 hover:text-white'
                  }`}
              >
                ALL ({tools.length})
              </button>
              {servers.map(srv => (
                <button
                  key={srv}
                  onClick={() => setSelectedServer(srv)}
                  className={`text-[7px] px-1.5 py-0.5 rounded font-bold uppercase transition-colors`}
                  style={{
                    background: selectedServer === srv ? SERVER_COLORS[srv] || '#6b7280' : '#0a0c10',
                    color: selectedServer === srv ? '#fff' : '#6b7280',
                  }}
                >
                  {SERVER_ICONS[srv] || '⚡'} {srv.replace('nexus-', '')}
                </button>
              ))}
            </div>
          </div>

          <div className="flex-1 overflow-y-auto bg-[#0a0c10] custom-scrollbar">
            {toolsLoading ? (
              <div className="p-4 text-center text-slate-600 text-[10px]">Loading tools...</div>
            ) : filteredTools.length === 0 ? (
              <div className="p-4 text-center text-slate-600 text-[10px]">No tools found</div>
            ) : (
              filteredTools.map((tool, i) => (
                <button
                  key={`${tool.server}-${tool.name}-${i}`}
                  onClick={() => {
                    setSelectedTool(tool);
                    setParams({});
                  }}
                  className={`w-full text-left p-2 border-b border-[#1e2130] hover:bg-[#1e2130] transition-colors ${selectedTool?.name === tool.name && selectedTool?.server === tool.server
                    ? 'bg-[#1e2130] border-l-2'
                    : ''
                    }`}
                  style={{
                    borderLeftColor:
                      selectedTool?.name === tool.name && selectedTool?.server === tool.server
                        ? SERVER_COLORS[tool.server] || '#6b7280'
                        : 'transparent',
                  }}
                >
                  <div className="flex items-center gap-1 mb-0.5">
                    <span className="text-[8px]">{SERVER_ICONS[tool.server] || '⚡'}</span>
                    <span className="text-[9px] font-bold text-white font-mono truncate">{tool.name}</span>
                  </div>
                  <p className="text-[8px] text-slate-500 leading-tight line-clamp-2">
                    {tool.description}
                  </p>
                  <span
                    className="text-[7px] px-1 py-0.5 rounded mt-1 inline-block font-bold"
                    style={{ background: (SERVER_COLORS[tool.server] || '#6b7280') + '22', color: SERVER_COLORS[tool.server] || '#6b7280' }}
                  >
                    {tool.server}
                  </span>
                </button>
              ))
            )}
          </div>
        </div>

        {/* QUICK ACTIONS PANEL */}
        <div key="quick_actions" className="panel-wrapper">
          <div className={`p-2 border-b border-[#2a2e39] bg-[#131722]/60 flex items-center justify-between ${!isLocked ? 'drag-handle' : 'cursor-default'} flex-shrink-0`}>
            <div className="flex items-center gap-2">
              <i className="fas fa-bolt text-yellow-500"></i>
              <h3 className="text-[10px] font-black text-slate-300 uppercase tracking-widest">Diagnostic Quick Actions</h3>
            </div>
            <div className="flex items-center gap-3">
              <span className="text-[8px] text-slate-500 uppercase tracking-widest">Active Servers: {servers.length}</span>
              <i className="fas fa-grip-lines text-slate-600"></i>
            </div>
          </div>
          <div className="flex-1 overflow-y-auto p-3 bg-[#0a0c10] custom-scrollbar flex items-center">
            <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-8 gap-2 w-full">
              {QUICK_ACTIONS.map(qa => (
                <button
                  key={qa.label}
                  onClick={() => executeTool(qa.server, qa.tool, qa.args)}
                  disabled={loading}
                  className="p-2 bg-[#1e2130] border border-[#2a2e39] rounded hover:border-blue-500 hover:bg-[#252a3a] transition-all text-left flex flex-col justify-center shadow-lg"
                >
                  <div className="flex items-center gap-2 mb-1">
                    <span className="text-sm">{SERVER_ICONS[qa.server] || '⚡'}</span>
                    <span className="text-[9px] font-bold text-white leading-tight">{qa.label}</span>
                  </div>
                  <div className="text-[8px] text-slate-500 font-mono truncate w-full">{qa.tool}</div>
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* EXECUTION PANEL */}
        <div key="execution" className="panel-wrapper">
          <div className={`p-2 border-b border-[#2a2e39] bg-[#131722]/60 flex items-center justify-between ${!isLocked ? 'drag-handle' : 'cursor-default'} flex-shrink-0`}>
            <div className="flex items-center gap-2">
              <i className="fas fa-play-circle text-green-500"></i>
              <h3 className="text-[10px] font-black text-slate-300 uppercase tracking-widest">Tool Execution</h3>
            </div>
            <i className="fas fa-grip-lines text-slate-600"></i>
          </div>
          <div className="flex-1 overflow-y-auto p-4 bg-[#0a0c10] custom-scrollbar">
            {!selectedTool ? (
              <div className="flex flex-col items-center justify-center h-full text-slate-600 opacity-50 gap-3">
                <i className="fas fa-hand-pointer text-4xl mb-2"></i>
                <p className="text-[10px] font-bold uppercase tracking-widest text-center">Select tool<br />from directory</p>
              </div>
            ) : (
              <div className="space-y-4 animate-in fade-in duration-300">
                <div className="p-3 bg-[#1e2130] border border-[#2a2e39] rounded shadow-inner">
                  <div className="flex items-center gap-2 mb-1.5">
                    <span className="text-xl">{SERVER_ICONS[selectedTool.server] || '⚡'}</span>
                    <div className="flex-1 min-w-0">
                      <h3 className="text-[11px] font-black text-white font-mono truncate">{selectedTool.name}</h3>
                      <span
                        className="text-[8px] px-1.5 py-0.5 rounded font-bold inline-block mt-0.5"
                        style={{ background: (SERVER_COLORS[selectedTool.server] || '#6b7280') + '33', color: SERVER_COLORS[selectedTool.server] || '#6b7280' }}
                      >
                        {selectedTool.server}
                      </span>
                    </div>
                  </div>
                  <p className="text-[9px] text-slate-400 mt-2">{selectedTool.description}</p>
                </div>

                {selectedTool.parameters?.properties && Object.keys(selectedTool.parameters.properties).length > 0 && (
                  <div className="p-3 bg-[#1e222d] border border border-[#2a2e39] rounded space-y-3 shadow-lg">
                    <h4 className="text-[9px] font-black text-slate-400 uppercase tracking-widest border-b border-[#2a2e39] pb-2">Parameters</h4>
                    {(Object.entries(selectedTool.parameters.properties) as [string, { type: string; description: string; default?: any }][]).map(([key, schema]) => {
                      const isRequired = selectedTool.parameters?.required?.includes(key);
                      return (
                        <div key={key}>
                          <label className="text-[9px] text-slate-300 flex items-center gap-1 mb-1 font-bold">
                            <span className="font-mono text-purple-300">{key}</span>
                            {isRequired && <span className="text-red-400">*</span>}
                            <span className="text-slate-600 font-normal">({schema.type})</span>
                          </label>
                          {schema.type === 'boolean' ? (
                            <select
                              value={params[key] ?? 'false'}
                              onChange={e => setParams(p => ({ ...p, [key]: e.target.value === 'true' }))}
                              className="w-full bg-[#131722] border border-[#2a2e39] rounded px-3 py-2 text-[10px] text-white focus:outline-none focus:border-blue-500"
                            >
                              <option value="false">false</option>
                              <option value="true">true</option>
                            </select>
                          ) : (
                            <input
                              type={schema.type === 'integer' || schema.type === 'number' ? 'number' : 'text'}
                              placeholder={schema.description}
                              value={params[key] ?? (schema.default !== undefined ? String(schema.default) : '')}
                              onChange={e => {
                                const val = schema.type === 'integer' ? parseInt(e.target.value) || 0
                                  : schema.type === 'number' ? parseFloat(e.target.value) || 0
                                    : e.target.value;
                                setParams(p => ({ ...p, [key]: val }));
                              }}
                              className="w-full bg-[#131722] border border-[#2a2e39] rounded px-3 py-2 text-[10px] text-white placeholder-slate-600 focus:outline-none focus:border-blue-500 font-mono"
                            />
                          )}
                          <p className="text-[8px] text-slate-500 mt-1 pl-1 border-l-2 border-slate-700">{schema.description}</p>
                        </div>
                      );
                    })}
                  </div>
                )}

                <button
                  onClick={handleExecuteSelected}
                  disabled={loading}
                  className="w-full py-3 bg-blue-600 hover:bg-blue-500 disabled:bg-slate-700 text-white text-[10px] font-black uppercase tracking-widest rounded shadow-xl transition-all"
                >
                  {loading ? '⏳ EXECUTING PIPELINE...' : '▶ DISPATCH PAYLOAD'}
                </button>
              </div>
            )}
          </div>
        </div>

        {/* RESULTS PANEL */}
        <div key="results" className="panel-wrapper">
          <div className={`p-2 border-b border-[#2a2e39] bg-[#131722]/60 flex items-center justify-between ${!isLocked ? 'drag-handle' : 'cursor-default'} flex-shrink-0`}>
            <div className="flex items-center gap-2">
              <i className="fas fa-stream text-cyan-500"></i>
              <h3 className="text-[10px] font-black text-slate-300 uppercase tracking-widest">Execution Results ({results.length})</h3>
            </div>
            <div className="flex items-center gap-2">
              {results.length > 0 && (
                <button
                  onClick={() => setResults([])}
                  className="text-[8px] text-slate-500 hover:text-red-400 uppercase font-black transition-colors px-2 py-0.5 border border-[#2a2e39] rounded bg-[#1e222d]"
                >
                  Clear Queue
                </button>
              )}
              <i className="fas fa-grip-lines text-slate-600"></i>
            </div>
          </div>
          <div className="flex-1 overflow-y-auto p-4 bg-[#0a0c10] custom-scrollbar space-y-3">
            {results.length === 0 ? (
              <div className="flex flex-col items-center justify-center h-full text-slate-600 opacity-50 gap-3">
                <i className="fas fa-check-double text-4xl mb-2"></i>
                <p className="text-[10px] font-bold uppercase tracking-widest text-center">No active<br />results loaded</p>
              </div>
            ) : (
              results.map((r, i) => (
                <div
                  key={i}
                  className={`p-3 bg-[#1e2130] border rounded shadow-md text-[10px] animate-in slide-in-from-top-2 ${r.error ? 'border-red-500/40' : 'border-l-4 border-l-cyan-500 border-t-[#2a2e39] border-r-[#2a2e39] border-b-[#2a2e39]'
                    }`}
                >
                  <div className="flex items-center justify-between mb-2 pb-2 border-b border-[#2a2e39]">
                    <div className="flex items-center gap-2">
                      <span>{SERVER_ICONS[r.server] || '⚡'}</span>
                      <span className="font-mono font-bold text-white text-[11px]">{r.tool}</span>
                      <span
                        className="text-[7px] px-1.5 py-0.5 rounded uppercase font-black"
                        style={{
                          background: (SERVER_COLORS[r.server] || '#6b7280') + '33',
                          color: SERVER_COLORS[r.server] || '#6b7280'
                        }}
                      >
                        {r.server}
                      </span>
                    </div>
                    <div className="flex items-center gap-3 text-[8px] font-mono text-slate-500">
                      <span>{r.timestamp}</span>
                      <span className="bg-[#131722] px-1.5 py-0.5 rounded">{r.duration_ms}ms</span>
                      {r.error ? (
                        <span className="text-red-400 font-bold tracking-widest bg-red-500/10 px-1.5 py-0.5 rounded">FAIL</span>
                      ) : (
                        <span className="text-green-400 font-bold tracking-widest bg-green-500/10 px-1.5 py-0.5 rounded">SUCCESS</span>
                      )}
                    </div>
                  </div>
                  {r.error ? (
                    <div className="text-red-400 font-mono text-[10px] bg-red-500/10 p-2 rounded border border-red-500/20">
                      {r.error}
                    </div>
                  ) : (
                    <pre className="text-cyan-300 font-mono text-[9px] bg-[#131722] p-3 rounded border border-[#2a2e39] overflow-x-auto max-h-64 overflow-y-auto custom-scrollbar">
                      {JSON.stringify(r.result, null, 2)}
                    </pre>
                  )}
                </div>
              ))
            )}
          </div>
        </div>

      </ResponsiveGridLayout>
    </div>
  );
};

export default MCPToolsPanel;
